import { useMemo, useState } from 'react'

import {
  type PimProductV11,
  PimProductV1StockStatusEnum,
  type PimProductWithVariantsV1,
  type PimVariantAttributeV1,
} from '@redteclab/api/clients/bully'

import {
  type AttributeGroups,
  type AttributeSelectionRenderGroups,
  type SelectionEntry,
} from './AttributeGroup'
import {
  calcProductPageAttributesSelectionState,
  filterVariantsMatchingAttributes,
} from './calcProductPageAttributesSelectionState'
import { getHasSingleRenderGroup } from './getShouldShowOnlyPackagingSizeOnMobile'
import { prepareRenderGroups } from './prepareRenderGroups'
import {
  type NotSelectedValidationErrorControls,
  useNotSelectedValidationError,
} from './useNotSelectedValidationError'

/**
 * replaces attribute with same name
 * input:
 * [{variant_attribute: 'color', value: 'red'}, {variant_attribute: 'size', value: 's'}]
 * {variant_attribute: 'color', value: 'black'}
 * output:
 * [{variant_attribute: 'color', value: 'black'}, {variant_attribute: 'size', value: 's'}]
 */
const addOrReplaceAttributeByName = (
  attributes: PimVariantAttributeV1[],
  newAttribute: PimVariantAttributeV1,
): PimVariantAttributeV1[] => {
  const newAttributes = attributes.filter(
    (attr) => attr.variant_attribute !== newAttribute.variant_attribute,
  )
  newAttributes.push(newAttribute)

  return newAttributes
}

export interface ProductPageContextAttributeSelectionControls {
  attributeNotSelectedErrorControls: NotSelectedValidationErrorControls
  handleAttributeSelect: (attribute: SelectionEntry) => void
  hasSingleRenderGroup: boolean
  isSingleRenderGroup: boolean
  renderGroups: AttributeSelectionRenderGroups
  selectionGroupsMap: AttributeGroups
  variantMatchingSelection?: PimProductV11
}

const onlyOneAttributeForAllVariants = (
  attribute: PimVariantAttributeV1,
  variants: PimProductV11[],
): boolean => {
  return !variants.some((variant) => {
    return variant.variant_attributes?.some((variantAttribute) => {
      return (
        variantAttribute.variant_attribute === attribute.variant_attribute &&
        variantAttribute.value !== attribute.value
      )
    })
  })
}

const getInitialSelection = (
  currentVariant: PimProductV11,
  isMasterPage: boolean,
  product: PimProductV11 & PimProductWithVariantsV1,
): PimVariantAttributeV1[] => {
  const currentVariantAttributes = currentVariant.variant_attributes ?? []
  if (!isMasterPage) {
    return currentVariantAttributes
  }

  /**
   * case of "master" page, personal attributes are not preselected.
   */
  const currentSelection = currentVariant.variant_attributes ?? []

  return currentSelection.filter((attribute) => {
    if (
      attribute.personal &&
      onlyOneAttributeForAllVariants(attribute, product.variants)
    ) {
      return true
    }

    return !attribute.personal
  })
}

const handleAttributeSelection = ({
  selectedAttributes,
  selectionEntry,
  setSelectedAttributes,
}: {
  selectedAttributes: PimVariantAttributeV1[]
  selectionEntry: SelectionEntry
  setSelectedAttributes: (selectedAttributes: PimVariantAttributeV1[]) => void
}): void => {
  const { allCandidateVariants, allowDeselection, attribute, isSelected } =
    selectionEntry

  const { edgecaseSetAttributesOfCandidateAsSelectedOnSelection } =
    selectionEntry

  /**
   * case when only one complete group we need to set attributes of candidate as selected
   * due to the attribute may have not all the attributes/more than previous current variant.
   */
  if (edgecaseSetAttributesOfCandidateAsSelectedOnSelection) {
    const candidateAttributes =
      allCandidateVariants[0]?.variant_attributes ?? []

    setSelectedAttributes(candidateAttributes)

    return
  }
  /**
   * same selected, remove from selected
   */
  if (isSelected) {
    if (allowDeselection) {
      setSelectedAttributes(
        selectedAttributes.filter(
          (attr) => attr.variant_attribute !== attribute.variant_attribute,
        ),
      )
    }

    return
  }

  /**
   * if no same type is selected, add it to selection.
   * if same type previsouly selected, replace it with the new one.
   */
  const updatedSelectedAttributes = addOrReplaceAttributeByName(
    selectedAttributes,
    attribute,
  )
  setSelectedAttributes(updatedSelectedAttributes)
}

/**
 * for attribute selection, only variants:
 * * that are sellable,
 * * or user directly landed on such variant
 * TODO: backend shall already return with filtered.
 */
const filterSellableVariants = (
  product: PimProductWithVariantsV1,
): PimProductWithVariantsV1 => {
  const primaryVariant = product.variants.find((it) => it.upid === product.upid)
  const sellableVariants = product.variants.filter((it) => {
    if (!it.stock_status) {
      return true
    }

    return (
      !(
        [
          PimProductV1StockStatusEnum.OutOfSale,
          PimProductV1StockStatusEnum.NotInCatalog,
        ] as PimProductV1StockStatusEnum[]
      ).includes(it.stock_status) || it.upid === primaryVariant?.upid
    )
  })

  return {
    ...product,
    variants: sellableVariants,
  }
}

/**
 * responsible for
 * - building the selection entries map representing data about the attributes and their selection state
 * - handles logic which of them are selected or have other variants.
 * - recalculates state whenever user selects other attributes.
 *
 * flows:
 * calculates selection state
 * user clicks on other attribute
 * state is recalculated
 * variant that matches selection is found and returned
 */
export const useProductPageAttributeSelectionControls = ({
  currentVariant,
  isMasterPage,
  productWithAllVariants,
}: {
  currentVariant: PimProductV11
  isMasterPage: boolean
  productWithAllVariants: PimProductWithVariantsV1
}): ProductPageContextAttributeSelectionControls => {
  /**
   * IMPORTANT: mutated product data will be used for attribute calculations and rendering. (e.g. some variants removed etc.)
   * so for product calculations only this func shall be source of truth.
   */
  const product = useMemo((): PimProductWithVariantsV1 => {
    return filterSellableVariants(productWithAllVariants)
  }, [productWithAllVariants])

  const [selectedAttributes, setSelectedAttributes] = useState<
    PimVariantAttributeV1[]
  >(() => {
    return getInitialSelection(currentVariant, isMasterPage, product)
  })

  // eslint-disable-next-line react-hooks/void-use-memo -- executes side effect immediately needed for header search popup, refactoring needed as useMemo used for memoization not side-effects
  useMemo(() => {
    // eslint-disable-next-line react-hooks/set-state-in-render -- executes side effect immediately needed for header search popup, refactoring needed as useMemo used for memoization not side-effects
    setSelectedAttributes(
      getInitialSelection(currentVariant, isMasterPage, product),
    )
  }, [product, currentVariant, isMasterPage])

  const selectionGroupsMap = useMemo(
    () =>
      calcProductPageAttributesSelectionState(
        product,
        selectedAttributes,
        currentVariant,
      ),
    [currentVariant, product, selectedAttributes],
  )

  const attributeNotSelectedErrorControls = useNotSelectedValidationError({
    product,
    selectionGroupsMap,
  })

  /**
   * after user selected some other variants this will find the matching variant for current selection and return it.
   * So parent e.g. can watch the value and on change replace/fetch the new variant.
   */
  const [variantMatchingSelection] = useMemo(
    () =>
      filterVariantsMatchingAttributes(product.variants, selectedAttributes),
    [product.variants, selectedAttributes],
  )

  const renderGroups = prepareRenderGroups(Object.values(selectionGroupsMap))

  const isSingleRenderGroup = renderGroups.length === 1
  const hasSingleRenderGroup = getHasSingleRenderGroup(
    renderGroups,
    selectionGroupsMap,
    isSingleRenderGroup,
  )

  return {
    attributeNotSelectedErrorControls,
    /**
     * update selected attribute and recalculate selection state
     */
    handleAttributeSelect: (selectionEntry: SelectionEntry): void => {
      handleAttributeSelection({
        selectedAttributes,
        selectionEntry,
        setSelectedAttributes,
      })
    },
    hasSingleRenderGroup,
    isSingleRenderGroup,
    renderGroups,
    selectionGroupsMap,
    variantMatchingSelection,
  }
}
